import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  MenuItem,
  CircularProgress,
  TextField,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Select,
  FormControl,
  InputLabel,
  Tab,
  Tabs,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, where, orderBy, getDoc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../../firebase';
import { useTheme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import imageCompression from 'browser-image-compression';

const POSITIONS = [
  'PRESIDENT',
  'VICE-PRESIDENT',
  'SECRETARY',
  'JOINT-SECRETARY',
  'TREASURER',
  'MEMBER',
  'CO-OPTED MEMBER'
];

const SINGLE_MEMBER_POSITIONS = [
  'PRESIDENT',
  'VICE-PRESIDENT',
  'SECRETARY',
  'JOINT-SECRETARY',
  'TREASURER'
];

const MULTI_MEMBER_POSITIONS = [
  'MEMBER',
  'CO-OPTED MEMBER'
];

// Generate financial years from 2011 to current year
const generateFinancialYears = () => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let year = 2011; year <= currentYear; year++) {
    years.unshift(`${year}-${year + 1}`);
  }
  return years;
};

function ManagingCommitteeManagement() {
  const theme = useTheme();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [selectedYear, setSelectedYear] = useState(generateFinancialYears()[0]);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [expandedAccordion, setExpandedAccordion] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    position: '',
    enrollmentNo: '',
    mobileNo: '',
    email: '',
    photoUrl: '',
    financialYear: '',
  });

  useEffect(() => {
    fetchMembers();
  }, [selectedYear]);

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const docRef = doc(db, 'managingCommittee', selectedYear);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const yearData = docSnap.data();
        const membersArray = yearData.members || [];
        setMembers(membersArray);
      } else {
        setMembers([]);
      }
    } catch (error) {
      console.error('Error fetching managing committee members:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Check file size - 1MB = 1024 * 1024 bytes
      if (file.size > 1024 * 1024) {
        alert('Image size should not exceed 1MB. Please choose a smaller image.');
        event.target.value = ''; // Reset file input
        return;
      }

      setPhotoFile(file);
      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({ ...prev, photoUrl: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadPhoto = async (file) => {
    try {
      // Compress image if needed
      let imageToUpload = file;
      if (file.size > 1024 * 1024) {
        const options = {
          maxSizeMB: 1,
          maxWidthOrHeight: 1200,
          useWebWorker: true
        };
        imageToUpload = await imageCompression(file, options);
      }

      const storageRef = ref(storage, `committee-photos/${selectedYear}/${Date.now()}-${file.name}`);
      const snapshot = await uploadBytes(storageRef, imageToUpload);
      return await getDownloadURL(snapshot.ref);
    } catch (error) {
      console.error('Error uploading photo:', error);
      throw error;
    }
  };

  const handleOpenDialog = (member = null) => {
    if (member) {
      setFormData({
        name: member.name,
        position: member.position,
        enrollmentNo: member.enrollmentNo,
        mobileNo: member.mobileNo,
        email: member.email,
        photoUrl: member.photoUrl,
        financialYear: member.financialYear
      });
      setEditingMember(member);
    } else {
      resetForm();
      setEditingMember(null);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingMember(null);
    setPhotoFile(null);
    setPhotoPreview('');
    setFormData({
      name: '',
      position: '',
      enrollmentNo: '',
      mobileNo: '',
      email: '',
      photoUrl: '',
      financialYear: selectedYear,
    });
  };

  const handleSubmit = async () => {
    try {
      const groupedMembers = groupMembersByPosition();
      const existingMembersInPosition = groupedMembers[formData.position] || [];

      // Validate for single member positions
      if (!isMultiMemberPosition(formData.position) && 
          existingMembersInPosition.length > 0 && 
          !editingMember) {
        alert(`${formData.position} position is already filled.`);
        return;
      }

      let photoUrl = formData.photoUrl;
      if (photoFile) {
        const options = {
          maxSizeMB: 1,
          maxWidthOrHeight: 1024,
          useWebWorker: true
        };
        
        const compressedFile = await imageCompression(photoFile, options);
        const storageRef = ref(storage, `committee_photos/${selectedYear}/${formData.name}_${Date.now()}`);
        const uploadResult = await uploadBytes(storageRef, compressedFile);
        photoUrl = await getDownloadURL(uploadResult.ref);
      }

      const memberData = {
        ...formData,
        photoUrl,
        financialYear: selectedYear
      };

      const docRef = doc(db, 'managingCommittee', selectedYear);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        let members = docSnap.data().members || [];
        
        if (editingMember) {
          members = members.map(m => m.id === editingMember.id ? { ...memberData, id: m.id } : m);
        } else {
          memberData.id = Date.now().toString();
          members.push(memberData);
        }
        
        await updateDoc(docRef, { members });
      } else {
        memberData.id = Date.now().toString();
        await setDoc(docRef, { members: [memberData] });
      }

      setOpenDialog(false);
      resetForm();
      fetchMembers();
    } catch (error) {
      console.error('Error saving member:', error);
      alert('Error saving member. Please try again.');
    }
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const groupMembersByPosition = () => {
    const grouped = {};
    POSITIONS.forEach(position => {
      grouped[position] = members.filter(member => member.position === position);
    });
    return grouped;
  };

  const isMultiMemberPosition = (position) => {
    return MULTI_MEMBER_POSITIONS.includes(position);
  };

  const renderMembersList = () => {
    const groupedMembers = groupMembersByPosition();
    
    return (
      <Grid container spacing={3}>
        {POSITIONS.map((position) => (
          <Grid item xs={12} key={position}>
            <Accordion 
              expanded={expandedAccordion === position}
              onChange={() => setExpandedAccordion(expandedAccordion === position ? false : position)}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ 
                  bgcolor: alpha(theme.palette.primary.main, 0.03),
                  '&.Mui-expanded': {
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 2 }}>
                  <Typography sx={{ fontWeight: 600 }}>
                    {position} ({groupedMembers[position].length})
                  </Typography>
                  {(isMultiMemberPosition(position) || groupedMembers[position].length === 0) && (
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenDialog(null);
                        setFormData(prev => ({ ...prev, position }));
                      }}
                      sx={{
                        bgcolor: '#580100',
                        '&:hover': {
                          bgcolor: '#3d0100',
                        }
                      }}
                    >
                      Add {position}
                    </Button>
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  {groupedMembers[position].map((member) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={member.id}>
                      <Card 
                        elevation={3}
                        sx={{ 
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          position: 'relative'
                        }}
                      >
                        <CardMedia
                          component="img"
                          image={member.photoUrl || '/placeholder-profile.jpg'}
                          alt={member.name}
                          sx={{ 
                            height: 200,
                            objectFit: 'cover'
                          }}
                        />
                        <CardContent sx={{ flexGrow: 1 }}>
                          <Typography gutterBottom variant="h6" component="div" sx={{ color: '#580100' }}>
                            {member.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            {member.position}
                          </Typography>
                          {member.enrollmentNo && (
                            <Typography variant="body2" color="text.secondary">
                              Enrollment No: {member.enrollmentNo}
                            </Typography>
                          )}
                          {member.mobileNo && (
                            <Typography variant="body2" color="text.secondary">
                              Mobile: {member.mobileNo}
                            </Typography>
                          )}
                          {member.email && (
                            <Typography variant="body2" color="text.secondary">
                              Email: {member.email}
                            </Typography>
                          )}
                        </CardContent>
                        <CardActions sx={{ justifyContent: 'flex-end', p: 2 }}>
                          <IconButton
                            onClick={() => handleOpenDialog(member)}
                            size="small"
                            sx={{
                              color: '#580100',
                              '&:hover': {
                                bgcolor: alpha('#580100', 0.04),
                              }
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </CardActions>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Grid>
        ))}
      </Grid>
    );
  };

  const resetForm = () => {
    setFormData({
      name: '',
      position: '',
      enrollmentNo: '',
      mobileNo: '',
      email: '',
      photoUrl: '',
      financialYear: selectedYear
    });
    setPhotoFile(null);
    setPhotoPreview('');
    setEditingMember(null);
  };

  return (
    <Box 
      sx={{ 
        flexGrow: 1,
        pl: { xs: 0, sm: '240px' },
        pt: { xs: 2, sm: 3 },
        pb: { xs: 2, sm: 3 },
        width: '100%',
        minHeight: 'calc(100vh - 64px)',
        bgcolor: '#f8f9fa'
      }}
    >
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography 
            variant="h4" 
            component="h1" 
            sx={{ 
              fontWeight: 600,
              color: '#580100',
            }}
          >
            Managing Committee Management
          </Typography>
        </Box>

        {/* Year Selection */}
        <Box sx={{ mb: 4 }}>
          <FormControl fullWidth sx={{ maxWidth: 300 }}>
            <InputLabel>Financial Year</InputLabel>
            <Select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              label="Financial Year"
            >
              {generateFinancialYears().map((year) => (
                <MenuItem key={year} value={year}>{year}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Committee Members Display */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          renderMembersList()
        )}
        {/* Add/Edit Dialog */}
        <Dialog 
          open={openDialog} 
          onClose={handleCloseDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ m: 0, p: 2 }}>
            {editingMember ? 'Edit Member' : 'Add New Member'}
            <IconButton
              onClick={handleCloseDialog}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: 'text.secondary',
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
                  <Avatar
                    src={photoPreview || formData.photoUrl}
                    sx={{ width: 100, height: 100, mb: 1 }}
                  />
                  <Button
                    component="label"
                    variant="outlined"
                    startIcon={<PhotoCamera />}
                    size="small"
                  >
                    Upload Photo
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={handlePhotoChange}
                    />
                  </Button>
                  <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                    Maximum file size: 1MB
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Name"
                  name="name"
                  value={formData.name}
                  onChange={handleFormChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Position</InputLabel>
                  <Select
                    name="position"
                    value={formData.position}
                    onChange={handleFormChange}
                    label="Position"
                  >
                    {POSITIONS.map((position) => (
                      <MenuItem key={position} value={position}>{position}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Enrollment No"
                  name="enrollmentNo"
                  value={formData.enrollmentNo}
                  onChange={handleFormChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Mobile No"
                  name="mobileNo"
                  value={formData.mobileNo}
                  onChange={handleFormChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleFormChange}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button 
              variant="contained"
              onClick={handleSubmit}
              sx={{
                bgcolor: '#580100',
                '&:hover': {
                  bgcolor: '#3d0100',
                }
              }}
            >
              {editingMember ? 'Save Changes' : 'Add Member'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
}

export default ManagingCommitteeManagement;
